import React, { useEffect, useRef, useState } from "react";
import { get } from "lodash";
import { useSelector, useDispatch } from "react-redux";
import { Form } from "@unform/web";
import { string } from "yup";
import { useLocation } from "react-router-dom";
import tvgConf from "@tvg/conf";
import RegularPageRegistration from "@tvg/atomic-ui/_templates/RegularPageRegistration";
import Button from "@tvg/atomic-ui/_atom/Buttons/button";
import { fontMedium } from "@tvg/atomic-ui/_static/Typography";
import { AddressForm } from "@tvg/atomic-ui/_organism/AddressForm";
import Checkbox from "@tvg/atomic-ui/_molecule/Checkbox";
import { REGISTRATION_STEPS } from "@tvg/atomic-ui/_static/Constants/progressBar";
import { Scope } from "@unform/core";
import usePrevious from "@tvg/sh-lib-my-bets/hooks/usePrevious";
import {
  handleAddressSubmit,
  onHomeStateChangeFn,
  cityOnChangeFn,
  streetOnChangeFn,
  onMailingZipChange,
  onHomeZipChange,
  updateAddressInRedux,
  onCheckMailing
} from "./handlers";
import { onBackButtonClickGtmEvent } from "../../utils/gtm/otherInteractions";
import { onVirtualPageViewGtmEvent } from "../../utils/gtm/pageViews";

import {
  handleBackButtonHeader,
  handleInputOnBlurGtm,
  handleSelectChangeGtm,
  stateOptions
} from "../../utils/handlers";

import {
  homeStreetValidation,
  homeCityValidation,
  mailingStreetValidation,
  mailingCityValidation,
  homeZipValidation,
  mailingZipValidation
} from "./utils";

import {
  getAddressFields,
  getAddressIsFormValid,
  getCurrentStep,
  getEnableRegAutoAddress,
  getIsEditing,
  getStateSelector,
  getRegistrationTextsPage,
  getWelcomeFields
} from "../../utils/selectors";
import {
  setCurrentStepRegistrationAction,
  setAddressFieldsRegistrationAction
} from "../../actions";

export const AddressComponent = ({ device }) => {
  const location = useLocation();
  const dispatch = useDispatch();

  const enableRegAutoAddress = useSelector(getEnableRegAutoAddress);
  const labels = useSelector((store) =>
    getRegistrationTextsPage("address")(store)
  );
  const currentStep = useSelector(getCurrentStep);
  const addressFields = useSelector(getAddressFields);
  const isFormValid = useSelector(getAddressIsFormValid);
  const welcomeFields = useSelector(getWelcomeFields);
  const isEditing = useSelector(getIsEditing);
  const homeAddressState = useSelector(getStateSelector("home"));
  const mailAddressState = useSelector(getStateSelector("mailing"));

  const formRef = useRef(null);

  const [hasMailingAddress, setHasMailingAddress] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [addressFieldsSaved, setAddressFieldsSaved] = useState(addressFields);
  const [hasPreloadedInputs, setHasPreloadedInputs] = useState(false);

  const prevHomeState = usePrevious(homeAddressState);
  const prevMailState = usePrevious(mailAddressState);

  useEffect(() => {
    const view = isEditing ? "edit-address-details" : "address-details";
    onVirtualPageViewGtmEvent(location, view);
  }, []);

  useEffect(() => {
    // Populate welcome fields for editing purposes
    formRef.current.setData(addressFields);

    // In order to not send GTM events when going back and forward,
    // this is to prevent that and only dispatch GTM after we have updated inputs
    const inputUpdate = setTimeout(() => {
      setHasPreloadedInputs(true);
    }, 0);

    return () => {
      clearTimeout(inputUpdate);
    };
  }, [JSON.stringify(addressFields)]);

  // In order to not send GTM events when going back and forward on select drop down,
  // this is to prevent that and only dispatch GTM after we have updated inputs
  useEffect(() => {
    if (
      (prevHomeState !== homeAddressState &&
        !isEditing &&
        prevHomeState !== undefined) ||
      (!!prevHomeState && isEditing)
    ) {
      handleSelectChangeGtm(
        "homeState",
        isEditing,
        "Address",
        homeAddressState
      );
    }

    if (
      (prevMailState !== mailAddressState &&
        !isEditing &&
        prevMailState !== undefined) ||
      (!!prevMailState && isEditing)
    ) {
      handleSelectChangeGtm(
        "mailingState",
        isEditing,
        "Address",
        mailAddressState
      );
    }
  }, [homeAddressState, mailAddressState]);

  useEffect(() => {
    if (addressFields.home.zip) {
      onHomeZipChange({
        formRef,
        dispatch,
        welcomeFields,
        zip: addressFields.home.zip
      });
    }
    setAddressFieldsSaved(addressFields);
  }, []);

  const onHomeStateChange = onHomeStateChangeFn({
    formRef,
    birth: welcomeFields.birth,
    dispatch
  });

  const isMobile = device === "mobile";
  const setInnerTitle = () => (isEditing ? "Edit Address" : "Address");

  return (
    <RegularPageRegistration
      device={device}
      brand={tvgConf().brand}
      handleBackButton={() => {
        onBackButtonClickGtmEvent(`${isEditing ? "Edit " : ""}Address`);
        if (isEditing) {
          dispatch(setAddressFieldsRegistrationAction(addressFieldsSaved));
          dispatch(setCurrentStepRegistrationAction(REGISTRATION_STEPS.REVIEW));
        } else {
          handleBackButtonHeader({
            dispatch,
            currentStep,
            offset: enableRegAutoAddress ? 0 : 1
          });
        }
      }}
      currentStep={isEditing ? REGISTRATION_STEPS.REVIEW : currentStep}
      isEditing={isEditing}
      isAddress
      infoText={
        !isMobile && !hasMailingAddress
          ? get(labels, "home.description", "")
          : null
      }
    >
      <Form
        ref={formRef}
        onSubmit={handleAddressSubmit({
          dispatch,
          formRef,
          hasMailingAddress,
          isEditing,
          isFormValid,
          labels,
          setIsFormDirty,
          welcomeFields
        })}
      >
        <Scope path="home">
          <AddressForm
            title={setInnerTitle()}
            hasPreloadedInputs={hasPreloadedInputs}
            streetOnBlur={handleInputOnBlurGtm(
              "homeStreet",
              homeStreetValidation(labels),
              isEditing,
              "Address"
            )}
            streetOnChange={streetOnChangeFn({
              isFormDirty,
              formRef,
              validation: homeStreetValidation(labels),
              dispatch,
              isEditing
            })}
            detailsOnBlur={handleInputOnBlurGtm(
              "homeDetails",
              string().min(1),
              isEditing,
              "Address"
            )}
            detailsOnChange={() => {
              updateAddressInRedux({ formRef, dispatch });
            }}
            cityOnBlur={handleInputOnBlurGtm(
              "homeCity",
              homeCityValidation(labels),
              isEditing,
              "Address"
            )}
            cityOnChange={cityOnChangeFn({
              formRef,
              isFormDirty,
              validation: homeCityValidation(labels),
              dispatch,
              isEditing
            })}
            zipOnBlur={handleInputOnBlurGtm(
              "homeZip",
              homeZipValidation(labels),
              isEditing,
              "Address"
            )}
            zipOnChange={(value) => {
              onHomeZipChange({
                formRef,
                dispatch,
                welcomeFields,
                zip: value
              });
            }}
            stateOnChange={(args) => {
              formRef.current.setFieldValue("home.zip", "");
              formRef.current.setFieldError("home.zip", "");
              formRef.current.setFieldValue("home.city", "");
              formRef.current.setFieldError("home.city", "");

              return onHomeStateChange(args);
            }}
            isFormDirty={isFormDirty}
            description={
              !isEditing ? get(labels, "home.description", "") : null
            }
            states={stateOptions}
            labels={labels.home}
            qaLabel="address-container"
            formSection="section-one"
            autoFocus={!!get(addressFields, "home.zip") !== true}
          />
        </Scope>
        <Checkbox
          id="checkbox-address"
          setChecked={setHasMailingAddress}
          name="checkbox"
          onClick={(event) => {
            onCheckMailing(event.target.value, isEditing);
          }}
          value={hasMailingAddress}
          label={labels.mailingAddressCheck}
          qaLabel="address-has-mailing-address"
        />
        <Scope path="mailing">
          <AddressForm
            hasPreloadedInputs={hasPreloadedInputs}
            streetOnBlur={handleInputOnBlurGtm(
              "mailingStreet",
              mailingStreetValidation(labels),
              isEditing,
              "Address"
            )}
            streetOnChange={streetOnChangeFn({
              isFormDirty,
              formRef,
              validation: mailingStreetValidation(labels),
              dispatch,
              isEditing
            })}
            detailsOnBlur={handleInputOnBlurGtm(
              "mailingDetails",
              string().min(1),
              isEditing,
              "Address"
            )}
            detailsOnChange={() => {
              updateAddressInRedux({ formRef, dispatch });
            }}
            cityOnBlur={handleInputOnBlurGtm(
              "mailingCity",
              mailingCityValidation(labels),
              isEditing,
              "Address"
            )}
            cityOnChange={cityOnChangeFn({
              formRef,
              isFormDirty,
              validation: mailingCityValidation(labels),
              dispatch,
              isEditing
            })}
            zipOnBlur={handleInputOnBlurGtm(
              "mailingZip",
              mailingZipValidation(labels),
              isEditing,
              "Address"
            )}
            zipOnChange={(value) => {
              onMailingZipChange({
                formRef,
                birth: welcomeFields.birth,
                dispatch,
                zip: value
              });
            }}
            stateOnChange={(args) => {
              formRef.current.setFieldValue("mailing.zip", "");
              formRef.current.setFieldValue("mailing.city", "");
              formRef.current.setFieldError("mailing.zip", "");
              formRef.current.setFieldError("mailing.city", "");

              updateAddressInRedux({
                dispatch,
                formRef
              });
            }}
            isFormDirty={isFormDirty}
            visible={hasMailingAddress}
            title={
              isEditing
                ? `Edit ${get(labels, "mailing.title")}`
                : get(labels, "mailing.title")
            }
            description={!isEditing ? get(labels, "mailing.description") : null}
            states={stateOptions}
            labels={labels.mailing}
            qaLabel="mailing-container"
            formSection="section-two"
            isLast
          />
        </Scope>
        <Button
          size="bigger"
          hasRoundedCorners
          isStretched
          isUppercase={false}
          hasShadow
          fontFamily={fontMedium}
          isBold={false}
          qaLabel="registration-submit-btn"
        >
          Continue
        </Button>
      </Form>
    </RegularPageRegistration>
  );
};

export default AddressComponent;
